import { VASTTracker } from '@dailymotion/vast-client'
import {
  ComponentPropsWithRef,
  forwardRef,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactPlayer from 'react-player'
import { clientConfig } from '../../../../client'
import {
  useGridBlock,
  usePageMeta,
  usePageViewInDay,
  usePageVisibility,
} from '../../../../contexts'
import { VAds } from '../../../../types'
import { dfpUtils, VastErrorType } from '../../../Player/Helpers'
import { ILevels } from '../../../Player/Models/PlayerModels'
import { AdChevron, AdPlayIcon, Close, VolumeControlIcon } from '../icons'
import { usePlayer } from '../PlayerContext'
import {
  BottomOverlay,
  AdLoadingSpinner,
  Container,
  GuiArea,
  MoreInfo,
  Player,
  PlayerAdOverlay,
  PlayIconOverlay,
  Progress,
  ProgressBarContainer,
  ProgressContainer,
  TopCloseContainer,
  TopMuteContainer,
  TopOverlay,
} from './VastPlayer.style'
import { prettyLog } from '../../../../utils'
import { reelsHe } from '../../../../locales/reels.he'

// Stable reference — `usePageMeta()?.levels ?? []` would otherwise create a
// brand-new empty array on every render (destructuring defaults always
// construct a fresh value), which broke the load-VAST effect's memoization
// and refetched a new ad on every VastPlayer re-render.
const EMPTY_LEVELS = [] as ILevels
/** after the user reaches the ad slide; an ad that is not playing by then is skipped */
export const AD_START_DEADLINE_MS = 3000
/** when the ad file was not loaded ahead (3g), it still has to download first */
export const AD_START_DEADLINE_NO_PRELOAD_MS = 6000

type IVastProps = {
  isActive: boolean
  // Fetch the VAST and buffer the creative while the slide is still off-screen,
  // so the ad is already on its first frame when the user swipes onto it.
  preFetch?: boolean
  /** download the ad file ahead too (fast networks); otherwise only its metadata */
  preloadMedia?: boolean
  index?: number
  overlay?: ReactNode
  onClose?: () => void
  onFinished?: () => void
  onError?: () => void
  /** the ad started (after the impression); the feed then lets the next slide load */
  onPlaying?: () => void
  /** 'none': only the ad video; the caller draws close, label and sound */
  chrome?: 'full' | 'none'
  /** drop an ad that has not started by then (defaults by preloadMedia) */
  startDeadlineMs?: number
  /** another ad request than the reels' (13 Shorts), read when it is sent */
  request?: IVastRequest
  /** a press is held on the ad: it waits until the release */
  held?: boolean
  /**
   * an ad that stops this long after it started (waiting for its data, or a
   * picture that froze while it should play) is dropped
   */
  stallDeadlineMs?: number
  /** gone while it plays (the page left): VAST hears it as a close */
  closeOnUnmount?: boolean
  /** what happens to the ad, for the caller's analytics and controls */
  onAdEvent?: (event: VastAdEvent) => void
}

/** a request on other units than the reels' */
export interface IVastRequest {
  /** under the network, e.g. 'Shorts/shorts_desktop' */
  adUnit: string
  componentType: string
  /** the cust_params platform */
  platform: string
  /** added to cust_params after the vertical player's own keys */
  custParams: [string, string][]
  /** how long each VAST hop may take (default: the vertical player's) */
  timeoutMs?: number
}

export interface IVastCreativeInfo {
  creativeId: string
  lineItemId: string
  /** seconds */
  duration: number
  orientation: 'vertical' | 'horizontal'
}

export type VastAdEvent =
  | { type: 'request' }
  | { type: 'impression'; creative: IVastCreativeInfo; startMuted: boolean }
  | { type: 'time'; time: number; duration: number }
  | { type: 'muted'; muted: boolean }
  /** the browser or the system paused it (not a hold, not a click away) */
  | { type: 'paused'; paused: boolean }
  | { type: 'complete'; creative: IVastCreativeInfo }
  | {
      type: 'error'
      errorType: VastErrorType | 'stall'
      /** the VAST error code that stands for it */
      errorCode: string
    }

const str = (value: unknown) =>
  typeof value === 'string'
    ? value.trim()
    : typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : ''

/**
 * The ids GAM puts in its VAST, read defensively (every field is optional):
 * the line item is the <Ad id>, the creative the <Creative id> (its adId is
 * the video asset's, not the creative's); a redirect's own are GAM's
 */
const readCreative = (
  data: VAds,
  video: HTMLVideoElement | undefined
): IVastCreativeInfo => {
  const ad = data.vastAd as { id?: unknown }
  const creative = data.vastCreative as {
    id?: unknown
    mediaFiles?: { fileURL?: unknown; width?: unknown; height?: unknown }[]
  }
  const fileDuration =
    video && Number.isFinite(video.duration) ? video.duration : 0
  // the picture's own size once it is known, else the VAST file's
  const file = Array.isArray(creative?.mediaFiles)
    ? creative.mediaFiles.find(f => f?.fileURL === data.video)
    : undefined
  const width = video?.videoWidth || Number(file?.width) || 0
  const height = video?.videoHeight || Number(file?.height) || 0
  return {
    creativeId: data.ids?.creativeId || str(creative?.id),
    lineItemId: data.ids?.lineItemId || str(ad?.id),
    duration: data.duration > 0 ? data.duration : fileDuration,
    orientation: width > height ? 'horizontal' : 'vertical',
  }
}

/** a click that opened no page in front of this one gives the ad back after it */
const CLICK_RETURN_MS = 1000

/** how often a playing ad's second is read, to find a picture that froze */
const PROGRESS_CHECK_MS = 250

/**
 * Calls `onStall` once the second `read` gives has not moved for `limitMs`.
 * Null (not meant to move: paused, held, hidden) starts the count again.
 * Counted in checks, so a page whose timers waited is not a frozen ad.
 * Returns the stop.
 */
export const watchProgress = (
  read: () => number | null,
  limitMs: number,
  onStall: () => void
) => {
  let last = read()
  let still = 0
  const timer = setInterval(() => {
    const time = read()
    if (time === null || time !== last) {
      last = time
      still = 0
      return
    }
    still += PROGRESS_CHECK_MS
    if (still >= limitMs) {
      clearInterval(timer)
      onStall()
    }
  }, PROGRESS_CHECK_MS)
  return () => clearInterval(timer)
}

/** a ClickThrough the browser can open as a page of its own, or '' */
const pageUrl = (template: string) => {
  try {
    const url = new URL(template, window.location.href)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : ''
  } catch {
    return ''
  }
}

type IProps = ComponentPropsWithRef<typeof ReactPlayer> & IVastProps

const ProgressBar = ({ progress }: { progress: number; duration?: number }) => (
  <ProgressContainer>
    <ProgressBarContainer>
      <Progress progress={progress} />
    </ProgressBarContainer>
  </ProgressContainer>
)

export type PlayerRef = {
  play: () => void
  pause: () => void
  isPlaying: boolean
  /**
   * A click on the ad: ClickTracking, and the advertiser's page in a new tab
   * (the ad waits until the user comes back). False when the ad has no page
   * of its own: then nothing happens.
   */
  click: () => boolean
  /** plays again after the browser or the system paused it */
  resume: () => void
  /** the user moved past the ad (VAST skip); the seconds it played */
  skip: () => number
  /** the player closed during the ad (VAST close) */
  close: () => void
  toggleMute: () => void
}

/**
 * All events emitted by VASTTracker (per the official docs).
 * VASTTracker extends a *custom* EventEmitter whose removeAllListeners()
 * requires an event name, so we iterate every known event explicitly.
 */
const VAST_TRACKER_EVENTS = [
  'creativeView',
  'start',
  'firstQuartile',
  'midpoint',
  'thirdQuartile',
  'complete',
  'rewind',
  'skip',
  'closeLinear',
  'close',
  'pause',
  'resume',
  'mute',
  'unmute',
  'fullscreen',
  'exitFullscreen',
  'playerExpand',
  'playerCollapse',
  'impression',
  'notUsed',
  'acceptableInvitationLinear',
  'adExpand',
  'adCollapse',
  'minimize',
  'overlayViewDuration',
  'otherAdInteraction',
  'progress',
  'clickthrough',
  'error',
  'TRACKER-error',
]

/**
 * Safely dispose a VASTTracker by removing all known event listeners.
 */
function disposeTracker(tracker: VASTTracker | null) {
  if (!tracker) {
    return
  }
  for (const event of VAST_TRACKER_EVENTS) {
    try {
      tracker.removeAllListeners(event)
    } catch {
      // ignore — event may not be registered
    }
  }
}

export const VastPlayer = forwardRef<PlayerRef, IProps>(
  (
    {
      isActive,
      preFetch = false,
      preloadMedia = true,
      overlay,
      onClose,
      onFinished,
      onError,
      onPlaying,
      chrome = 'full',
      startDeadlineMs,
      request,
      held = false,
      stallDeadlineMs,
      closeOnUnmount = false,
      onAdEvent,
      ...props
    },
    ref
  ) => {
    // read when they happen: an inline callback or a new request object must
    // not load the ad again
    const onAdEventRef = useRef(onAdEvent)
    onAdEventRef.current = onAdEvent
    const requestRef = useRef(request)
    requestRef.current = request
    const closeOnUnmountRef = useRef(closeOnUnmount)
    closeOnUnmountRef.current = closeOnUnmount
    const heldRef = useRef(held)
    heldRef.current = held
    // kept out of `...props`: ReactPlayer must not receive it, and handleStart is memoized once
    const onPlayingRef = useRef(onPlaying)
    useEffect(() => {
      onPlayingRef.current = onPlaying
    }, [onPlaying])
    // the next slide fetches its ad early; it may play only once it is active
    const shouldLoad = isActive || preFetch
    const isActiveRef = useRef(isActive)
    useEffect(() => {
      isActiveRef.current = isActive
    }, [isActive])
    const playerRef = useRef<ReactPlayer>(null)
    const trackerRef = useRef<VASTTracker | null>(null)
    const adTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Refs that must survive re-renders without triggering them
    const hasStartedRef = useRef(false)
    const impressionTrackedRef = useRef(false)
    const wasNearEndRef = useRef(false)
    const finishedRef = useRef(false)
    // Set once the browser has refused sound-on autoplay and we retried muted,
    // so we don't loop between the two.
    const autoplayFallbackRef = useRef(false)
    // Guard against setState calls after unmount
    const isMountedRef = useRef(true)

    const {
      isMiniSeries,
      isBigBrother,
      componentType,
      isMuted: blockMuted,
      muteForced,
      toggleMute: toggleBlockMute,
      reportMute: reportBlockMute,
    } = usePlayer()
    // a mute the browser forced on the clips is not the user's choice: the ad
    // asks for sound itself
    const ownChoice = blockMuted && !muteForced

    // The vertical ad starts with sound — it only starts muted when the block
    // itself is configured that way, or when the browser refuses sound-on
    // autoplay (see `startAd`).
    const [isMuted, setIsMuted] = useState(ownChoice)
    const [vastData, setVastData] = useState<VAds | null>(null)
    const [video, setVideo] = useState('')
    const [duration, setDuration] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false) // start false; driven by isActive+vastData
    const isPlayingRef = useRef(isPlaying)
    isPlayingRef.current = isPlaying
    const [videoClick, setVideoClick] = useState('')
    const [progress, setProgress] = useState(0)
    const [afterPageIsNotVisible, setAfterPageIsNotVisible] = useState(false)
    const [wasPaused, setWasPaused] = useState(true)
    // in the viewer: a landscape creative is letterboxed instead of filling
    const [wide, setWide] = useState(false)
    // in the viewer: the loader stays until the creative plays
    const [adPlaying, setAdPlaying] = useState(false)

    // the creative's ids and length, read once it is ready
    const creativeRef = useRef<IVastCreativeInfo | null>(null)

    const isPageVisible = usePageVisibility()
    const { levels = EMPTY_LEVELS, pageType } = usePageMeta() || {}
    const pageTypeRef = useRef(pageType)
    pageTypeRef.current = pageType
    const { gridId } = useGridBlock()
    const { pageViewInDayCount } = usePageViewInDay()

    // ─── mount / unmount guard ────────────────────────────────────────────────
    useEffect(() => {
      isMountedRef.current = true
      return () => {
        isMountedRef.current = false
        if (
          closeOnUnmountRef.current &&
          hasStartedRef.current &&
          !finishedRef.current
        ) {
          finishedRef.current = true
          trackerRef.current?.close()
        }
        // Hard-stop playback and release tracker on unmount
        setIsPlaying(false)
        disposeTracker(trackerRef.current)
        trackerRef.current = null
      }
    }, [])

    // ─── helpers ──────────────────────────────────────────────────────────────
    const safeSet = useCallback(
      <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
        (value: React.SetStateAction<T>) => {
          if (isMountedRef.current) {
            setter(value)
          }
        },
      []
    )

    // `onFinished` is passed inline by the parent (a fresh function every
    // render), so it's kept in a ref and `finishAd` stays referentially
    // stable — otherwise it would invalidate the load-VAST effect below on
    // every parent re-render.
    const onFinishedRef = useRef(onFinished)
    useEffect(() => {
      onFinishedRef.current = onFinished
    }, [onFinished])

    // a prefetched ad that fails before the user reaches it is handled by the
    // feed (PlayerView.handleAdFinished): removed when it is ahead of the
    // active slide, which does not move what is on screen
    const finishAd = useCallback(() => {
      if (finishedRef.current) {
        return
      }
      finishedRef.current = true
      onFinishedRef.current?.()
    }, [])

    // ─── reset when deactivated ───────────────────────────────────────────────
    // Progress is kept: an ad that stays prefetched next to the active slide
    // resumes where it was paused. Dropping the ad resets it (load cleanup).
    useEffect(() => {
      if (!isActive) {
        safeSet(setIsPlaying)(false)
      }
    }, [isActive, safeSet])

    // ─── load VAST ad ─────────────────────────────────────────────────────────
    useEffect(() => {
      if (!shouldLoad) {
        return
      }

      let cancelled = false
      finishedRef.current = false

      const loadVast = async () => {
        // its values are the ones of this moment: each request is built anew
        const asked = requestRef.current
        onAdEventRef.current?.({ type: 'request' })
        const vastResult = await dfpUtils.parseVast({
          pageViewedInDay: pageViewInDayCount,
          environment: clientConfig.adTargetEnv || 'prod',
          ComponentType: asked?.componentType ?? componentType,
          componentId: gridId,
          assetId: '',
          referenceId: '',
          levels,
          ...(asked && {
            adUnit: asked.adUnit,
            platform: asked.platform,
            pageType: pageTypeRef.current || undefined,
            extraCustParams: asked.custParams,
            timeoutMs: asked.timeoutMs,
          }),
        })

        if (cancelled || !isMountedRef.current) {
          return false
        }

        if (!vastResult.hasAds) {
          prettyLog(
            'VastPlayer: 217',
            { msg: 'Error fetching ads', err: vastResult.error },
            'warning'
          )
          onAdEventRef.current?.({
            type: 'error',
            errorType: vastResult.errorType,
            errorCode: vastResult.errorCode,
          })
          return false
        }

        const data = vastResult.data
        return {
          url: data.url,
          duration: data.duration,
          video: data.video,
          vastAd: data.vastAd,
          vastCreative: data.vastCreative,
          // GAM's own ids when its answer redirected to another server
          ...(data.ids && { ids: data.ids }),
        }
      }

      loadVast().then(data => {
        // a cancelled load (the user moved on) is not a missing ad: finishing
        // here would remove the slide and jump the feed back
        if (cancelled) {
          return
        }
        if (!data) {
          finishAd()
          return
        }
        safeSet(setVastData)(data)
      })

      return () => {
        cancelled = true
        // Also reset vastData so a stale ad can't play during re-activation
        if (isMountedRef.current) {
          setVastData(null)
          setVideo('')
          setDuration(0)
          setVideoClick('')
          setProgress(0)
        }
      }
    }, [
      gridId,
      levels,
      pageViewInDayCount,
      isBigBrother,
      isMiniSeries,
      componentType,
      safeSet,
      finishAd,
      shouldLoad,
    ])

    // ─── pause / resume pixels ────────────────────────────────────────────────
    // vast-client starts with no pause state and sends 'resume' for the first
    // setPaused(false): only a pause of a started ad, and the play after it,
    // reach the tracker
    const trackedPauseRef = useRef(false)
    const trackPaused = useCallback((paused: boolean) => {
      if (!hasStartedRef.current || paused === trackedPauseRef.current) {
        return
      }
      trackedPauseRef.current = paused
      trackerRef.current?.setPaused(paused)
    }, [])

    // ─── wire up VASTTracker when ad data arrives ─────────────────────────────
    useEffect(() => {
      // Dispose the previous tracker before creating a new one
      disposeTracker(trackerRef.current)
      trackerRef.current = null

      if (!vastData?.vastAd || !vastData?.vastCreative) {
        return
      }

      const tracker = new VASTTracker(
        undefined as never,
        vastData.vastAd as never,
        vastData.vastCreative as never
      )
      tracker.setDuration(vastData.duration)
      trackerRef.current = tracker

      // Reset per-ad tracking flags
      hasStartedRef.current = false
      trackedPauseRef.current = false
      impressionTrackedRef.current = false
      wasNearEndRef.current = false
      autoplayFallbackRef.current = false

      creativeRef.current = null
      safeSet(setVideo)(vastData.video)
      safeSet(setDuration)(vastData.duration)
      safeSet(setVideoClick)(vastData.url)

      return () => {
        // Clean up if vastData changes before the ad finishes
        disposeTracker(tracker)
        if (trackerRef.current === tracker) {
          trackerRef.current = null
        }
      }
    }, [vastData, safeSet])

    // ─── page visibility ──────────────────────────────────────────────────────
    useEffect(() => {
      if (!playerRef.current) {
        return
      }

      if (isPageVisible) {
        if (afterPageIsNotVisible) {
          safeSet(setAfterPageIsNotVisible)(false)
          if (isActive && !wasPaused) {
            safeSet(setIsPlaying)(true)
          }
        }
      } else if (!afterPageIsNotVisible) {
        const prevPaused = !isPlaying
        safeSet(setAfterPageIsNotVisible)(true)
        safeSet(setIsPlaying)(false)
        safeSet(setWasPaused)(prevPaused)
        trackPaused(true)
      }
    }, [
      isPageVisible,
      isActive,
      isPlaying,
      wasPaused,
      afterPageIsNotVisible,
      safeSet,
      trackPaused,
    ])

    // ─── drive isPlaying from isActive + vastData (and a held press) ──────────
    useEffect(() => {
      safeSet(setIsPlaying)(isActive && !!vastData && !held)
    }, [isActive, vastData, held, safeSet])

    const vastDataRef = useRef(vastData)
    vastDataRef.current = vastData
    // the ad's second, for a skip's report
    const playedRef = useRef(0)

    // ─── a click on the ad: its page opens in front, the ad waits ────────────
    // until the user is back (or at once when no page took the screen)
    const [awayForClick, setAwayForClick] = useState(false)
    const awayForClickRef = useRef(awayForClick)
    awayForClickRef.current = awayForClick
    useEffect(() => {
      if (!awayForClick) {
        return undefined
      }
      let left = false
      const back = () => {
        setAwayForClick(false)
        safeSet(setIsPlaying)(isActiveRef.current)
      }
      const onVisibility = () => {
        if (document.visibilityState === 'hidden') {
          left = true
        } else if (left) {
          back()
        }
      }
      const onBlur = () => {
        left = true
      }
      const onFocus = () => {
        if (left) {
          back()
        }
      }
      const timer = setTimeout(() => {
        if (!left) {
          back()
        }
      }, CLICK_RETURN_MS)
      document.addEventListener('visibilitychange', onVisibility)
      window.addEventListener('blur', onBlur)
      window.addEventListener('focus', onFocus)
      return () => {
        clearTimeout(timer)
        document.removeEventListener('visibilitychange', onVisibility)
        window.removeEventListener('blur', onBlur)
        window.removeEventListener('focus', onFocus)
      }
    }, [awayForClick, safeSet])

    // ─── player event handlers ────────────────────────────────────────────────
    const handleProgress = useCallback(
      (data: { played: number; playedSeconds: number }) => {
        if (!isMountedRef.current) {
          return
        }
        setProgress(data.played * 100)
        // a prefetched ad reports its first frame too: only a started one
        // reaches the tracker, so 'start' never comes before the impression
        if (hasStartedRef.current) {
          trackerRef.current?.setProgress(data.playedSeconds)
        }
        playedRef.current = data.playedSeconds
        if (onAdEventRef.current) {
          const length = playerRef.current?.getDuration()
          onAdEventRef.current({
            type: 'time',
            time: data.playedSeconds,
            duration:
              typeof length === 'number' &&
              Number.isFinite(length) &&
              length > 0
                ? length
                : 0,
          })
        }
      },
      []
    )

    const failAd = useCallback(() => {
      if (adTimeoutRef.current) {
        clearTimeout(adTimeoutRef.current)
        onAdEventRef.current?.({
          type: 'error',
          errorType: 'vast_error',
          errorCode: '400',
        })
        onError && onError()
      }
    }, [onError])

    /**
     * Kick off playback with sound. Browsers only allow sound-on autoplay while
     * the document holds user activation — the reel is opened by a tap, so it
     * normally does — but when the policy blocks us we retry muted instead of
     * dropping the ad, so autoplay never breaks.
     */
    const startAd = useCallback(() => {
      const videoEl = playerRef.current?.getInternalPlayer() as
        | HTMLVideoElement
        | undefined
      if (!videoEl) {
        return
      }
      videoEl.play().catch((err: DOMException) => {
        // a pause before the first frame (a held press, a hidden page) is
        // not a failure: the ad starts when it may play again
        if (err?.name === 'AbortError') {
          return
        }
        const blockedWithSound =
          err?.name === 'NotAllowedError' &&
          !videoEl.muted &&
          !autoplayFallbackRef.current
        if (!blockedWithSound) {
          failAd()
          return
        }
        // Retry muted so the ad still plays — the mute button brings the
        // sound back on the first tap. Logged because inside the app shells
        // this is the signal that the WebView still requires a user gesture
        // for audible playback.
        prettyLog(
          'VastPlayer: sound-on autoplay blocked, falling back to muted',
          { userAgent: navigator.userAgent },
          'warning'
        )
        autoplayFallbackRef.current = true
        videoEl.muted = true
        safeSet(setIsMuted)(true)
        trackerRef.current?.setMuted(true)
        videoEl.play().catch((retryErr: DOMException) => {
          if (retryErr?.name !== 'AbortError') {
            failAd()
          }
        })
      })
    }, [failAd, safeSet])

    const handleReady = useCallback(() => {
      trackerRef.current?.load()
      // A prefetched ad becomes ready while its slide is still off-screen: it
      // waits paused on its first frame, and `isPlaying` starts it through
      // react-player once the slide turns active. Only the first start is
      // made here, and only while it may play: 'canplay' comes again after a
      // stall, and an ad held, hidden, paused or waiting for the user's return
      // stays paused until `isPlaying` says so.
      if (
        isActiveRef.current &&
        isPlayingRef.current &&
        !hasStartedRef.current
      ) {
        startAd()
      }
    }, [startAd])

    const handleReadyInViewer = useCallback(() => {
      const videoEl = playerRef.current?.getInternalPlayer() as
        | HTMLVideoElement
        | undefined
      setWide(!!videoEl && videoEl.videoWidth > videoEl.videoHeight)
      handleReady()
    }, [handleReady])

    // iOS Safari clamps `preload` to metadata and paints nothing for a video
    // that hasn't played yet, which would leave a prefetched slide blank. A tiny
    // seek makes it decode and show the creative's first frame.
    const handleLoadedMetadata = useCallback(
      (e: SyntheticEvent<HTMLVideoElement>) => {
        const videoEl = e.currentTarget
        if (!hasStartedRef.current && videoEl.currentTime === 0) {
          videoEl.currentTime = 0.001
        }
      },
      []
    )

    // the ad file is downloaded ahead only on a fast link (preloadMedia)
    const preload = isActive || preloadMedia ? 'auto' : 'metadata'
    const playerConfig = useMemo(
      () => ({
        file: {
          attributes: {
            preload,
            onLoadedMetadata: handleLoadedMetadata,
          },
        },
      }),
      [preload, handleLoadedMetadata]
    )

    const handleStart = useCallback(() => {
      if (adTimeoutRef.current) {
        clearTimeout(adTimeoutRef.current)
      }
      if (!isMountedRef.current) {
        return
      }
      props.onStart?.()
      const videoEl = playerRef.current?.getInternalPlayer() as
        | HTMLVideoElement
        | undefined
      if (!impressionTrackedRef.current) {
        trackerRef.current?.trackImpression()
        impressionTrackedRef.current = true
        const data = vastDataRef.current
        if (data) {
          creativeRef.current = readCreative(data, videoEl)
          onAdEventRef.current?.({
            type: 'impression',
            creative: creativeRef.current,
            startMuted: !!videoEl?.muted,
          })
        }
      }
      if (!hasStartedRef.current) {
        hasStartedRef.current = true
        onPlayingRef.current?.()
      }
      if (videoEl) {
        trackerRef.current?.setMuted(videoEl.muted)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleStartInViewer = useCallback(() => {
      setAdPlaying(true)
      handleStart()
    }, [handleStart])

    // a pause nobody asked for here (the system took the sound, the browser
    // stopped it): the caller offers a way back
    const pausedByOthersRef = useRef(false)
    const handlePlay = useCallback(() => {
      trackPaused(false)
      if (pausedByOthersRef.current) {
        pausedByOthersRef.current = false
        onAdEventRef.current?.({ type: 'paused', paused: false })
      }
    }, [trackPaused])

    // ─── an ad stuck mid-play waits this long, then gives the clip back ──────
    // (a pause stops the wait: a held or hidden ad is not stuck)
    const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const clearStall = useCallback(() => {
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current)
        stallTimerRef.current = null
      }
    }, [])
    useEffect(() => clearStall, [clearStall])

    const handlePause = useCallback(() => {
      clearStall()
      const videoEl = playerRef.current?.getInternalPlayer() as
        | HTMLVideoElement
        | undefined
      // the end pauses it too, just before 'ended': that is no pause
      if (!videoEl?.ended) {
        trackPaused(true)
      }
      if (
        isActiveRef.current &&
        hasStartedRef.current &&
        !finishedRef.current &&
        !heldRef.current &&
        !awayForClickRef.current &&
        // the end pauses it too, just before 'ended'
        !videoEl?.ended &&
        document.visibilityState === 'visible'
      ) {
        pausedByOthersRef.current = true
        onAdEventRef.current?.({ type: 'paused', paused: true })
      }
    }, [clearStall, trackPaused])

    const dropStalled = useCallback(() => {
      if (isMountedRef.current && isActiveRef.current && !finishedRef.current) {
        onAdEventRef.current?.({
          type: 'error',
          errorType: 'stall',
          errorCode: '402',
        })
        finishAd()
      }
    }, [finishAd])

    const handleBuffer = useCallback(() => {
      if (
        !stallDeadlineMs ||
        !hasStartedRef.current ||
        finishedRef.current ||
        stallTimerRef.current
      ) {
        return
      }
      stallTimerRef.current = setTimeout(() => {
        stallTimerRef.current = null
        dropStalled()
      }, stallDeadlineMs)
    }, [stallDeadlineMs, dropStalled])

    // a picture can also freeze without 'waiting': its second stops moving
    // while it should play (not held, hidden, away on the advertiser's page or
    // stopped by the system with its Play showing)
    useEffect(() => {
      if (!stallDeadlineMs || !isPlaying) {
        return undefined
      }
      return watchProgress(
        () => {
          const videoEl = playerRef.current?.getInternalPlayer() as
            | HTMLVideoElement
            | undefined
          return videoEl &&
            isActiveRef.current &&
            hasStartedRef.current &&
            !finishedRef.current &&
            !heldRef.current &&
            !awayForClickRef.current &&
            !pausedByOthersRef.current &&
            document.visibilityState === 'visible'
            ? videoEl.currentTime
            : null
        },
        stallDeadlineMs,
        dropStalled
      )
    }, [stallDeadlineMs, isPlaying, dropStalled])

    const handleEnded = useCallback(() => {
      clearStall()
      trackerRef.current?.complete()
      if (!finishedRef.current && creativeRef.current) {
        onAdEventRef.current?.({
          type: 'complete',
          creative: creativeRef.current,
        })
      }
      finishAd()
    }, [finishAd, clearStall])

    const handleError = useCallback(
      (error?: unknown) => {
        // react-player funnels its own rejected play() promise in here. A
        // refused sound-on autoplay is not an ad error: retry once through
        // `startAd`, which falls back to muted playback, instead of dropping
        // the ad.
        if ((error as DOMException)?.name === 'NotAllowedError') {
          if (!autoplayFallbackRef.current) {
            startAd()
          }
          return
        }
        // a play() cut short by a pause (a click away, a hold, a swipe) is not
        // a media error: the ad is still there
        if ((error as DOMException)?.name === 'AbortError') {
          return
        }
        trackerRef.current?.error({ ERRORCODE: '403' })
        if (!finishedRef.current) {
          onAdEventRef.current?.({
            type: 'error',
            errorType: 'vast_error',
            errorCode: '403',
          })
        }
        finishAd()
      },
      [finishAd, startAd]
    )

    const handleGuiClick = useCallback(() => {
      safeSet(setIsPlaying)(prev => !prev)
    }, [safeSet])

    const handleToggleMute = useCallback(() => {
      const nextMuted = !isMuted
      // Toggling by hand is a user gesture, so the autoplay policy no longer
      // stands in the way of sound.
      autoplayFallbackRef.current = false
      // Flip the element here rather than waiting for React to push the
      // `muted` prop: WebKit only lets a video become audible while the user
      // activation from this tap is still live, and it pauses playback for a
      // video that unmutes outside of one.
      const videoEl = playerRef.current?.getInternalPlayer() as
        | HTMLVideoElement
        | undefined
      if (videoEl) {
        videoEl.muted = nextMuted
      }
      safeSet(setIsMuted)(nextMuted)
      trackerRef.current?.setMuted(nextMuted)
      // the user's choice holds for the clips after the ad too
      if (blockMuted !== nextMuted) {
        toggleBlockMute()
      } else {
        // the clips already sound this way (the browser muted the ad alone):
        // the press is still reported
        reportBlockMute(nextMuted)
      }
    }, [isMuted, safeSet, blockMuted, toggleBlockMute, reportBlockMute])

    // ─── imperative handle ────────────────────────────────────────────────────
    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          if (isMountedRef.current) {
            safeSet(setIsPlaying)(true)
          }
        },
        pause: () => {
          if (isMountedRef.current) {
            safeSet(setIsPlaying)(false)
          }
        },
        isPlaying,
        click: () => {
          const tracker = trackerRef.current
          // no page of its own (no ClickThrough a browser can open): a click
          // does nothing, not even ClickTracking
          if (
            !tracker ||
            !hasStartedRef.current ||
            finishedRef.current ||
            !pageUrl(vastDataRef.current?.url ?? '')
          ) {
            return false
          }
          let opened = false
          // sent at once, inside the click: the browser lets it open a tab
          const open = (resolved: unknown) => {
            const url = typeof resolved === 'string' ? pageUrl(resolved) : ''
            const page = url ? window.open(url, '_blank') : null
            if (page) {
              // the advertiser's page gets no hold on this one
              page.opener = null
              opened = true
            }
          }
          // emitted synchronously, after the ClickTracking pixels. A tab the
          // browser refused is still a click for GAM, so for the caller too
          tracker.on('clickthrough', open)
          tracker.click()
          tracker.off('clickthrough', open)
          if (opened) {
            safeSet(setIsPlaying)(false)
            setAwayForClick(true)
          }
          return true
        },
        resume: () => {
          const videoEl = playerRef.current?.getInternalPlayer() as
            | HTMLVideoElement
            | undefined
          if (videoEl && isActiveRef.current && !finishedRef.current) {
            safeSet(setIsPlaying)(true)
            videoEl.play().catch(() => undefined)
          }
        },
        skip: () => {
          if (!hasStartedRef.current || finishedRef.current) {
            return 0
          }
          finishedRef.current = true
          clearStall()
          trackerRef.current?.skip()
          safeSet(setIsPlaying)(false)
          return playedRef.current
        },
        close: () => {
          if (!hasStartedRef.current || finishedRef.current) {
            return
          }
          finishedRef.current = true
          clearStall()
          trackerRef.current?.close()
        },
        toggleMute: handleToggleMute,
      }),
      [isPlaying, safeSet, clearStall, handleToggleMute]
    )

    // the ad follows the sound choice made in the feed, also one made after the
    // feed opened (it used to copy it once, when the slide was created)
    useEffect(() => {
      if (isActive) {
        autoplayFallbackRef.current = false
        safeSet(setIsMuted)(ownChoice)
      }
    }, [isActive, ownChoice, safeSet])

    // the sound the ad really plays with (a refused autoplay mutes it alone).
    // Told again when the ad goes on screen: the viewer drops what an ad
    // loading ahead reports, and the button would show the clips' sound
    useEffect(() => {
      if (isActive) {
        onAdEventRef.current?.({ type: 'muted', muted: isMuted })
      }
    }, [isMuted, isActive])

    useEffect(() => {
      // counted from the moment the slide is active, whether or not the VAST
      // answer is in yet; a prefetched ad is never dropped before it is seen.
      // A press held on it stops the clock, and the release starts it again
      if (!finishAd || !isActive || held) {
        return
      }

      adTimeoutRef.current = setTimeout(() => {
        if (!hasStartedRef.current && isMountedRef.current) {
          if (!finishedRef.current) {
            onAdEventRef.current?.({
              type: 'error',
              errorType: 'timeout',
              // the answer came in time, the file did not
              errorCode: vastDataRef.current ? '402' : '301',
            })
          }
          finishAd()
        }
      }, startDeadlineMs ?? (preloadMedia ? AD_START_DEADLINE_MS : AD_START_DEADLINE_NO_PRELOAD_MS))

      return () => {
        if (adTimeoutRef.current) {
          clearTimeout(adTimeoutRef.current)
        }
      }
    }, [finishAd, isActive, held, preloadMedia, startDeadlineMs])

    if (chrome === 'none') {
      return (
        <Container $fill $wide={wide}>
          {video && vastData && (
            <Player
              ref={playerRef}
              url={video}
              controls={false}
              playsinline={true}
              onProgress={handleProgress}
              onReady={handleReadyInViewer}
              onPlay={handlePlay}
              onStart={handleStartInViewer}
              onPause={handlePause}
              onEnded={handleEnded}
              onError={handleError}
              onBuffer={handleBuffer}
              onBufferEnd={clearStall}
              playing={isPlaying}
              progressInterval={250}
              muted={isMuted}
              loop={false}
              {...props}
              width='100%'
              height='100%'
              config={playerConfig}
            />
          )}
          {isActive && !adPlaying && (
            <AdLoadingSpinner role='status' aria-label={reelsHe.loading} />
          )}
        </Container>
      )
    }

    return (
      <Container>
        {!(video && vastData) && (
          // while the ad loads the user can still leave
          <PlayerAdOverlay>
            {isActive && (
              <AdLoadingSpinner role='status' aria-label={reelsHe.loading} />
            )}
            <TopOverlay>
              <TopCloseContainer
                type='button'
                aria-label={reelsHe.close}
                onClick={onClose}
              >
                <Close />
              </TopCloseContainer>
            </TopOverlay>
          </PlayerAdOverlay>
        )}
        {video && vastData && (
          <>
            <Player
              ref={playerRef}
              url={video}
              controls={false}
              playsinline={true}
              onProgress={handleProgress}
              onReady={handleReady}
              onPlay={handlePlay}
              onStart={handleStart}
              onPause={handlePause}
              onEnded={handleEnded}
              onError={handleError}
              playing={isPlaying}
              progressInterval={250}
              muted={isMuted}
              loop={false}
              {...props}
              config={playerConfig}
            />
            <PlayerAdOverlay>
              <TopOverlay>
                <TopCloseContainer
                  type='button'
                  aria-label={reelsHe.close}
                  onClick={onClose}
                >
                  <Close />
                </TopCloseContainer>
                <TopMuteContainer
                  type='button'
                  aria-label={isMuted ? reelsHe.unmute : reelsHe.mute}
                  onClick={handleToggleMute}
                >
                  <VolumeControlIcon isMuted={isMuted} />
                </TopMuteContainer>
              </TopOverlay>
              <GuiArea onClick={handleGuiClick}>
                {overlay}
                {!isPlaying && (
                  <PlayIconOverlay>
                    <AdPlayIcon />
                  </PlayIconOverlay>
                )}
              </GuiArea>
              <BottomOverlay>
                {vastData.url && (
                  <MoreInfo
                    href={videoClick}
                    target='_blank'
                    ariaLabel='למידע נוסף'
                  >
                    <div>למידע נוסף</div>
                    <AdChevron />
                  </MoreInfo>
                )}
                <ProgressBar progress={progress} duration={duration} />
              </BottomOverlay>
            </PlayerAdOverlay>
          </>
        )}
      </Container>
    )
  }
)

VastPlayer.displayName = 'VastReactPlayer'
